import React from "react";

/**
 * CanvasRenderer - renders a canvas of cards as an SVG, with optional minimap.
 * @param {Object[]} cards - Array of card objects: { card_id, x, y, width, height, background }
 * @param {number} width - Width of the SVG canvas
 * @param {number} height - Height of the SVG canvas
 * @param {string} mode - 'full' | 'minimap'
 * @param {Object} viewport - { x, y, width, height } (for minimap viewport rectangle)
 * @param {boolean} showMiniMap - Whether to render a minimap inside the full canvas
 * @param {number} scale - Optional scale factor for preview
 * @param {string} language - Language of the cards
 * @param {Object[]} symbols - Array of symbol objects: { symbol_id, symbol }
 */
const CanvasRenderer = ({
  cards = [],
  width = 800,
  height = 600,
  mode = "full",
  viewport = null,
  showMiniMap = false,
  scale = 1,
  language = "en",
  symbols = [],
  showImages = false,
}) => {
  // Scale all positions and sizes for preview/minimap
  const s = scale || 1;
  const scaledWidth = width * s;
  const scaledHeight = height * s;

  // Helper: render SVG text with symbol references
  function renderSVGTextWithSymbols(cardText, x, y, fontSize, cardWidth, cardHeight) {
    if (!cardText) return null;
    const symbolRegex = /\{\{symbol:(\d+)\}\}/gi;
    const matches = [...cardText.matchAll(symbolRegex)];
    const cleanText = cardText.replace(symbolRegex, '').trim();
    const symbolIds = matches.map(m => parseInt(m[1], 10));

    const lineGap = fontSize * 0.6;
    const symbolSize = fontSize * 1.2;
    const totalSymbolsWidth = symbolIds.length * symbolSize;

    const elements = [];

    // Text paragraph (single block)
    if (cleanText) {
      elements.push(
        <text
          key="text"
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize, fontWeight: 500, fill: '#222', pointerEvents: 'none' }}
        >
          {cleanText}
        </text>
      );
    }

    // Symbols paragraph below text
    const symbolsY = y + fontSize + lineGap;
    const startX = x - totalSymbolsWidth / 2;
    symbolIds.forEach((id, idx) => {
      const symbol = symbols.find(s => s.symbol_id === id);
      if (symbol && symbol.symbol) {
        elements.push(
          <image
            key={`symbol-${idx}`}
            href={symbol.symbol}
            x={startX + idx * symbolSize}
            y={symbolsY}
            width={symbolSize}
            height={symbolSize}
            style={{ pointerEvents: 'none' }}
          />
        );
      }
    });

    return elements;
  }

  // Helper: get haze color for a color tag (matches GridCanvasRenderer.getHazeColor)
  function getHazeColor(color) {
    switch (color) {
      case 'red': return 'rgba(200, 0, 0, 0.65)';
      case 'green': return 'rgba(0, 200, 0, 0.65)';
      case 'blue': return 'rgba(0, 75, 200, 0.65)';
      case 'purple': return 'rgba(150, 0, 200, 0.75)';
      case 'yellow': return 'rgba(200, 150, 0, 0.65)';
      case 'grey': return 'rgba(60, 60, 60, 0.75)';
      default: return 'transparent';
    }
  }

  // Helper: extract color tag from card text
  function extractColorTag(cardText) {
    if (!cardText) return null;
    const match = cardText.match(/^\{\{(red|blue|grey|purple|green|yellow)\}\}/i);
    return match ? match[1].toLowerCase() : null;
  }

  return (
    <div style={{
      width: scaledWidth,
      height: scaledHeight,
      position: "relative",
      overflow: "hidden"
    }}>
      <svg
        width={scaledWidth}
        height={scaledHeight}
        style={{ display: "block" }}
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Render all cards as images with text overlays */}
        {cards.map(card => {
          // Extract color tag for minimap haze from the selected text option
          // (same logic as GridCanvasRenderer: use selected_option_index if available)
          const selectedOptionIdx = card.selected_option_index;
          const selectedTextObj = (card.texts || []).find(t => t.option_index === selectedOptionIdx) || (card.texts || [])[0];
          const cardText = selectedTextObj ? selectedTextObj.text : '';
          const colorTagMatch = cardText.match(/^\{\{(red|blue|grey|purple|green|yellow)\}\}/i);
          const colorTag = colorTagMatch ? colorTagMatch[1].toLowerCase() : null;

          return (
            <g key={card.card_id}>
              <image
                href={card.background}
                x={card.x}
                y={card.y}
                width={card.width}
                height={card.height}
                style={{
                  rx: 8,
                  ry: 8,
                  filter: mode === "minimap" ? "brightness(0.8)" : "none"
                }}
                />
              {/* Color haze overlay in minimap mode (matches GridCanvasRenderer) */}
              {mode === "minimap" && colorTag && (
                <rect
                  x={card.x}
                  y={card.y}
                  width={card.width}
                  height={card.height}
                  rx={8}
                  ry={8}
                  fill={getHazeColor(colorTag)}
                  style={{ mixBlendMode: 'color', pointerEvents: 'none' }}
                />
              )}
              {/* Text overlay */}
              {cardText && mode !== "minimap" && (
                renderSVGTextWithSymbols(
                  cardText,
                  card.x + card.width / 2,
                  card.y + card.height / 2,
                  Math.max(8, card.width / 15),
                  card.width,
                  card.height
                )
              )}
            </g>
          );
        })}
        {/* Minimap viewport rectangle */}
        {mode === "minimap" && viewport && (
          <rect
            x={viewport.x}
            y={viewport.y}
            width={viewport.width}
            height={viewport.height}
            fill="rgba(238, 255, 0, 0)"
            stroke="rgba(238, 255, 0, 0.8)"
            strokeWidth={0.2}
          />
        )}
      </svg>
    </div>
  );
};

export default CanvasRenderer; 